import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { evidenceApi, googleDriveApi, learningHooksApi, tasksApi } from '../../services/api';

interface LearningHook {
  id: number;
  title: string;
}

interface Task {
  id: number;
  learning_hook: number;
  stage: string;
  title: string;
  instructions: string;
  rubric: string;
  accepted_evidence_types?: string[];
  google_template_file_id?: string | null;
  google_template_name?: string | null;
  google_template_mime_type?: string | null;
  order: number;
  is_required: boolean;
  is_active: boolean;
}

interface EvidenceQueueItem {
  id: number;
  task: number;
  task_title: string;
  hook_id: number;
  hook_title: string;
  stage: string;
  student: number;
  student_email: string;
  status: string;
  submission_text?: string;
  submitted_at: string | null;
}

const STAGES = ['why', 'define', 'do', 'explain', 'apply', 'reflect'];
const EVIDENCE_TYPES: { value: string; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'link', label: 'Link' },
  { value: 'google_drive', label: 'Google Drive' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'camera', label: 'Camera' },
  { value: 'note_time_log', label: 'Note/Time Log' },
];

export const TasksEvidenceTest: React.FC = () => {
  const [hooks, setHooks] = useState<LearningHook[]>([]);
  const [selectedHookId, setSelectedHookId] = useState<number | null>(null);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [queue, setQueue] = useState<EvidenceQueueItem[]>([]);
  const [queueStatus, setQueueStatus] = useState<'submitted' | 'approved' | 'needs_revision'>('submitted');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newStage, setNewStage] = useState('define');
  const [newTitle, setNewTitle] = useState('');
  const [newInstructions, setNewInstructions] = useState('');
  const [newRequired, setNewRequired] = useState(true);
  const [newOrder, setNewOrder] = useState(0);
  const [newAcceptedEvidenceTypes, setNewAcceptedEvidenceTypes] = useState<string[]>(['text']);

  const [newTemplateFile, setNewTemplateFile] = useState<{ id: string; name: string; mimeType?: string } | null>(null);
  const [lastCreatedTaskId, setLastCreatedTaskId] = useState<number | null>(null);

  const loadGooglePicker = useCallback(async () => {
    if ((window as any).google?.picker) return;

    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[data-google-picker="1"]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Google API')));
        return;
      }

      const s = document.createElement('script');
      s.src = 'https://apis.google.com/js/api.js';
      s.async = true;
      s.defer = true;
      s.setAttribute('data-google-picker', '1');
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Google API'));
      document.body.appendChild(s);
    });

    await new Promise<void>((resolve, reject) => {
      (window as any).gapi.load('picker', {
        callback: () => resolve(),
        ontimeout: () => reject(new Error('Failed to load Google Picker')),
        timeout: 10000,
      });
    });
  }, []);

  const openTemplatePicker = useCallback(async () => {
    setError('');
    setSuccess('');

    const devKey = (process.env.REACT_APP_GOOGLE_PICKER_API_KEY || '').trim();
    if (!devKey) {
      setError('Missing REACT_APP_GOOGLE_PICKER_API_KEY (Google Picker API key)');
      return;
    }

    try {
      await loadGooglePicker();
      const tokenRes = await googleDriveApi.accessToken();
      const accessToken = tokenRes?.data?.access_token;
      const pickerAppId = String(tokenRes?.data?.picker_app_id || '');
      if (!accessToken || !/^\d+$/.test(pickerAppId)) {
        setError('Google Picker credentials are incomplete');
        return;
      }

      const picker = new (window as any).google.picker.PickerBuilder()
        .setDeveloperKey(devKey)
        .setOAuthToken(accessToken)
        .setAppId(pickerAppId)
        .setOrigin(window.location.origin)
        .addView(new (window as any).google.picker.DocsView().setIncludeFolders(false))
        .setCallback((data: any) => {
          if (data.action === (window as any).google.picker.Action.PICKED) {
            const doc = data.docs && data.docs[0];
            if (doc?.id) {
              setNewTemplateFile({ id: doc.id, name: doc.name, mimeType: doc.mimeType });
            }
          }
        })
        .build();
      picker.setVisible(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to open Google Picker');
    }
  }, [loadGooglePicker]);

  const hookTitle = useMemo(() => {
    const h = hooks.find(x => x.id === selectedHookId);
    return h?.title || '';
  }, [hooks, selectedHookId]);

  const fetchHooks = useCallback(async () => {
    const hooksResponse = await learningHooksApi.getAll();
    const hooksData = hooksResponse.data.learning_hooks || hooksResponse.data || [];
    const minimal = (Array.isArray(hooksData) ? hooksData : []).map((h: any) => ({
      id: h.id,
      title: h.title,
    }));
    setHooks(minimal);
    if (minimal.length > 0 && selectedHookId == null) {
      setSelectedHookId(minimal[0].id);
    }
  }, [selectedHookId]);

  const fetchTasks = useCallback(async (hookId: number) => {
    const res = await tasksApi.list({ hook_id: hookId });
    setTasks(res.data.tasks || []);
  }, []);

  const fetchQueue = useCallback(async (hookId: number) => {
    const res = await evidenceApi.reviewQueue({ hook_id: hookId, status: queueStatus });
    setQueue(res.data.evidence || []);
  }, [queueStatus]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await fetchHooks();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load learning hooks');
    } finally {
      setLoading(false);
    }
  }, [fetchHooks]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const run = async () => {
      if (!selectedHookId) return;
      setError('');
      setSuccess('');
      try {
        await Promise.all([
          fetchTasks(selectedHookId),
          fetchQueue(selectedHookId),
        ]);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to fetch tasks/evidence');
      }
    };
    run();
  }, [fetchQueue, fetchTasks, selectedHookId]);

  const handleCreateTask = async () => {
    if (!selectedHookId) return;
    setError('');
    setSuccess('');
    try {
      const createRes = await tasksApi.create({
        learning_hook: selectedHookId,
        stage: newStage,
        title: newTitle,
        instructions: newInstructions,
        accepted_evidence_types: newAcceptedEvidenceTypes,
        order: newOrder,
        is_required: newRequired,
        is_active: true,
      });

      const createdId = createRes?.data?.id;
      if (typeof createdId === 'number') {
        setLastCreatedTaskId(createdId);
      }

      if ((newAcceptedEvidenceTypes || []).indexOf('google_drive') !== -1 && newTemplateFile && typeof createdId === 'number') {
        await tasksApi.setDriveTemplate(createdId, {
          google_template_file_id: newTemplateFile.id,
          google_template_name: newTemplateFile.name,
          google_template_mime_type: newTemplateFile.mimeType || null,
        });
      }

      setNewTitle('');
      setNewInstructions('');
      setNewAcceptedEvidenceTypes(['text']);
      setNewTemplateFile(null);
      setSuccess('Task created');
      await fetchTasks(selectedHookId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to create task');
    }
  };

  const handleReview = async (evidenceId: number, decision: 'approved' | 'needs_revision') => {
    setError('');
    setSuccess('');
    const notes = window.prompt(`Notes for ${decision}:`, '') || '';
    try {
      await evidenceApi.review(evidenceId, { decision, notes });
      setSuccess('Evidence reviewed');
      if (selectedHookId) {
        await fetchQueue(selectedHookId);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to review evidence');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">Loading…</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tasks & Evidence (Test Page)</h1>
            <p className="text-gray-600 text-sm">Teacher-only test surface for Phase 1 endpoints.</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Learning Hook</label>
          <select
            value={selectedHookId ?? ''}
            onChange={(e) => setSelectedHookId(Number(e.target.value) || null)}
            className="w-full border rounded px-3 py-2"
          >
            {hooks.map(h => (
              <option key={h.id} value={h.id}>{h.title}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-semibold mb-3">Create Task</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                <select value={newStage} onChange={(e) => setNewStage(e.target.value)} className="w-full border rounded px-3 py-2">
                  {STAGES.map(s => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                <textarea value={newInstructions} onChange={(e) => setNewInstructions(e.target.value)} className="w-full border rounded px-3 py-2" rows={4} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Accepted evidence</label>
                <div className="grid grid-cols-2 gap-2">
                  {EVIDENCE_TYPES.map((t) => {
                    const checked = newAcceptedEvidenceTypes.indexOf(t.value) !== -1;
                    return (
                      <label key={t.value} className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const nextChecked = e.target.checked;
                            setNewAcceptedEvidenceTypes((prev) => {
                              const curr = Array.isArray(prev) ? prev : [];
                              if (nextChecked) {
                                return curr.indexOf(t.value) !== -1 ? curr : [...curr, t.value];
                              }
                              const filtered = curr.filter((x) => x !== t.value);
                              return filtered.length > 0 ? filtered : ['text'];
                            });
                          }}
                        />
                        <span>{t.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {newAcceptedEvidenceTypes.indexOf('google_drive') !== -1 && (
                <div className="border rounded p-3 bg-gray-50">
                  <div className="text-sm font-medium text-gray-800">Provide a template</div>
                  <div className="text-xs text-gray-600 mt-1">Choose a Google Doc/Slides/Sheets file from Drive.</div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <button
                      onClick={openTemplatePicker}
                      className="bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50"
                      type="button"
                    >
                      Choose from Drive
                    </button>
                    <div className="text-xs text-gray-700 truncate">
                      {newTemplateFile ? (
                        <span>Selected: <span className="font-medium">{newTemplateFile.name}</span></span>
                      ) : (
                        <span>No template selected</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} />
                  <span className="text-sm text-gray-700">Required</span>
                </label>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Order</span>
                  <input
                    type="number"
                    value={newOrder}
                    onChange={(e) => setNewOrder(Number(e.target.value) || 0)}
                    className="w-24 border rounded px-2 py-1"
                  />
                </div>
              </div>

              <button
                onClick={handleCreateTask}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                disabled={!selectedHookId || !newTitle.trim()}
              >
                Create Task
              </button>
            </div>

            <h2 className="text-lg font-semibold mt-8 mb-3">Tasks for: {hookTitle}</h2>
            {tasks.length === 0 ? (
              <div className="text-sm text-gray-500">No tasks yet.</div>
            ) : (
              <div className="space-y-2">
                {tasks.map(t => (
                  <div key={t.id} className="border rounded p-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">#{t.id} [{t.stage}] {t.title}</div>
                      <div className="text-xs text-gray-500">order {t.order} • {t.is_required ? 'required' : 'optional'}</div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Accepted: {(t.accepted_evidence_types && t.accepted_evidence_types.length > 0 ? t.accepted_evidence_types : ['text']).join(', ')}
                    </div>
                    {(t.google_template_file_id || '') && (
                      <div className="text-xs text-gray-500 mt-1">
                        Template: {t.google_template_name || t.google_template_file_id}
                      </div>
                    )}
                    {t.instructions && <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{t.instructions}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Evidence Review Queue ({queueStatus})</h2>
              <select
                value={queueStatus}
                onChange={(e) => setQueueStatus(e.target.value as any)}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value="submitted">submitted</option>
                <option value="approved">approved</option>
                <option value="needs_revision">needs_revision</option>
              </select>
            </div>
            {queue.length === 0 ? (
              <div className="text-sm text-gray-500">No submitted evidence to review.</div>
            ) : (
              <div className="space-y-2">
                {queue.map(item => (
                  <div key={item.id} className="border rounded p-3">
                    <div className="font-medium">[{item.stage}] {item.task_title}</div>
                    <div className="text-sm text-gray-600">Student: {item.student_email}</div>
                    <div className="text-xs text-gray-500">Submitted: {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : '—'}</div>

                    <div className="mt-2 text-sm text-gray-800 whitespace-pre-wrap break-words">
                      {(() => {
                        const raw = (item.submission_text || '').trim();
                        if (!raw) return '—';
                        const isLink = (raw.startsWith('http://') || raw.startsWith('https://')) && raw.indexOf(' ') === -1;
                        if (!isLink) return raw;
                        return (
                          <a href={raw} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                            {raw}
                          </a>
                        );
                      })()}
                    </div>

                    {queueStatus === 'submitted' && (
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => handleReview(item.id, 'approved')}
                          className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReview(item.id, 'needs_revision')}
                          className="bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 text-sm"
                        >
                          Needs revision
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
